// Meta (Facebook) identifier cookies for CAPI match quality.
// The RudderStack Facebook Pixel destination runs in cloud mode, so no base
// pixel (fbevents.js) loads to set _fbp/_fbc — we persist them ourselves in
// the formats Meta's Conversions API expects:
//   _fbp: fb.1.<creationTime>.<randomNumber>
//   _fbc: fb.1.<creationTime>.<fbclid>
// Cookies are written on the registrable domain (.pliability.com) so the
// Framer marketing site and the PWA share the same values.

import { getCookie } from "@/util/cookie";

const NINETY_DAYS_SECONDS = 90 * 24 * 60 * 60;

const getParam = (name: string): string => {
  if (typeof window === "undefined") return "";
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
};

const cookieDomain = (): string => {
  if (typeof window === "undefined") return "";
  const parts = window.location.hostname.split(".");
  if (parts.length < 2) return ""; // localhost etc. — host-only cookie
  return `.${parts.slice(-2).join(".")}`;
};

const setCookie = (name: string, value: string): void => {
  if (typeof document === "undefined") return;
  let cookie = `${name}=${value}; path=/; max-age=${NINETY_DAYS_SECONDS}; SameSite=Lax`;
  const domain = cookieDomain();
  if (domain) cookie += `; domain=${domain}`;
  // biome-ignore lint/suspicious/noDocumentCookie: first-party attribution cookie, cookieStore is not available in Safari
  document.cookie = cookie;
};

const buildFbc = (fbclid: string): string => `fb.1.${Date.now()}.${fbclid}`;

// Persist the Meta identifiers so they survive navigation from the ad-click
// landing URL through to signup/subscribe. Idempotent; safe to call on boot.
export const ensureMetaCookies = (): void => {
  if (typeof window === "undefined") return;

  const fbclid = getParam("fbclid");
  if (fbclid) {
    const existing = getCookie("_fbc");
    // A fresh click supersedes a stale cookie, matching base-pixel behavior.
    if (!existing || !existing.endsWith(`.${fbclid}`)) {
      setCookie("_fbc", buildFbc(fbclid));
    }
  }

  if (!getCookie("_fbp")) {
    const random = Math.floor(Math.random() * 2147483647);
    setCookie("_fbp", `fb.1.${Date.now()}.${random}`);
  }
};

// Trait keys match the server events' segment_attributes contract, which the
// RudderStack→Meta transformations already read (forwarded as fbp/fbc).
export const getMetaTraits = (): Record<string, string> => {
  const traits: Record<string, string> = {};

  const fbp = getCookie("_fbp");
  const fbc =
    getCookie("_fbc") ||
    (getParam("fbclid") ? buildFbc(getParam("fbclid")) : "");

  // eslint-disable-next-line camelcase
  if (fbp) traits.facebook_browser_id = fbp;
  // eslint-disable-next-line camelcase
  if (fbc) traits.facebook_click_id = fbc;

  return traits;
};
